import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Document as PDFDocument, Page as PDFPage, pdfjs} from 'react-pdf';
import MinusIcon from '@heroicons/react/20/solid/MinusIcon';
import PlusIcon from '@heroicons/react/20/solid/PlusIcon';
import InformationCircleIcon from '@heroicons/react/24/outline/InformationCircleIcon';
import DocumentTextIcon from '@heroicons/react/24/outline/DocumentTextIcon';
import ExclamationTriangleIcon from '@heroicons/react/24/outline/ExclamationTriangleIcon';
import ButtonIcon from '../../button/button-icon';
import Tooltip from '../../tooltip';
import {clampZoom, ZOOM_MIN, ZOOM_MAX, ZOOM_STEP} from './zoom-helpers';
import {
    resolvePreviewKind,
    PREVIEW_KIND_IMAGE,
    PREVIEW_KIND_NONE,
    PREVIEW_KIND_PDF,
} from './preview-helpers';
import '../../file-viewer/components/drivers/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

function PreviewPlaceholder({icon, message}) {
    return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-10 bg-tm-gray-100">
            {icon}

            <span className="text-base text-tm-gray-900">{message}</span>
        </div>
    );
}

export default function DocumentPreview({file, translate: translateProp, currentPage, onPagesNumberChange}) {
    const containerRef = useRef(null);
    const dragState = useRef(null);
    const ctrlHeldRef = useRef(false);

    const [zoom, setZoom] = useState(() => 1);

    // The shell always threads its own translate down, but this component is also a
    // plain export — degrade to the raw key instead of throwing if it is omitted.
    const translate = typeof translateProp === 'function' ? translateProp : ((key) => key);

    const previewKind = resolvePreviewKind(file);
    const isPdf = previewKind === PREVIEW_KIND_PDF;

    // Only images need a blob URL — react-pdf takes the File object itself, so creating
    // one for a PDF would pin a second reference to a possibly multi-MB file for nothing.
    const fileUrl = useMemo(
        () => (previewKind === PREVIEW_KIND_IMAGE ? URL.createObjectURL(file) : ''),
        [file, previewKind],
    );

    function handleWheelZoom(e) {
        if (!e.ctrlKey && !e.metaKey) {
            return;
        }

        e.preventDefault();
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        setZoom((prev) => clampZoom(prev, delta));
    }

    function handleDragStart(e) {
        if (e.button !== 0 || (!e.ctrlKey && !e.metaKey) || !containerRef.current) {
            return;
        }

        e.preventDefault();
        dragState.current = {
            startX: e.clientX,
            startY: e.clientY,
            scrollLeft: containerRef.current.scrollLeft,
            scrollTop: containerRef.current.scrollTop,
        };
        containerRef.current.style.cursor = 'grabbing';
    }

    function handleDragMove(e) {
        if (!dragState.current || !containerRef.current) {
            return;
        }

        containerRef.current.scrollLeft = dragState.current.scrollLeft - (e.clientX - dragState.current.startX);
        containerRef.current.scrollTop = dragState.current.scrollTop - (e.clientY - dragState.current.startY);
    }

    function handleDragEnd() {
        dragState.current = null;
        if (containerRef.current) {
            containerRef.current.style.cursor = ctrlHeldRef.current ? 'grab' : 'default';
        }
    }

    // Both key handlers keep the cursor alone while a drag is in progress: keydown repeats
    // fire for as long as Ctrl is held, and letting go of Ctrl mid-pan must not flip the
    // cursor away from `grabbing` while the drag is still running. handleDragEnd restores
    // the right cursor from ctrlHeldRef once the drag finishes.
    function handleKeyDown(e) {
        if (e.key !== 'Control' && e.key !== 'Meta') {
            return;
        }

        ctrlHeldRef.current = true;

        if (containerRef.current && !dragState.current) {
            containerRef.current.style.cursor = 'grab';
        }
    }

    function handleKeyUp(e) {
        if (e.key !== 'Control' && e.key !== 'Meta') {
            return;
        }

        ctrlHeldRef.current = false;

        if (containerRef.current && !dragState.current) {
            containerRef.current.style.cursor = 'default';
        }
    }

    function handleWindowBlur() {
        ctrlHeldRef.current = false;
        dragState.current = null;
        if (containerRef.current) {
            containerRef.current.style.cursor = 'default';
        }
    }

    useEffect(() => {
        return () => {
            if (fileUrl) {
                URL.revokeObjectURL(fileUrl);
            }
        };
    }, [fileUrl]);

    // Keyed on previewKind because the placeholder branches below render no scroll
    // container at all — the effect has to re-run once a previewable file arrives,
    // otherwise the wheel/mousedown listeners would never find an element to bind to.
    useEffect(() => {
        const el = containerRef.current;
        el?.addEventListener('wheel', handleWheelZoom, {passive: false});
        el?.addEventListener('mousedown', handleDragStart);
        document.addEventListener('mousemove', handleDragMove);
        document.addEventListener('mouseup', handleDragEnd);
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', handleWindowBlur);

        return () => {
            el?.removeEventListener('wheel', handleWheelZoom);
            el?.removeEventListener('mousedown', handleDragStart);
            document.removeEventListener('mousemove', handleDragMove);
            document.removeEventListener('mouseup', handleDragEnd);
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', handleWindowBlur);
        };
    }, [previewKind]);

    if (previewKind === PREVIEW_KIND_NONE) {
        return (
            <PreviewPlaceholder
                icon={<DocumentTextIcon className="w-12 h-12 text-tm-gray-400"/>}
                message={translate('text.no_file_selected')}
            />
        );
    }

    if (!isPdf && previewKind !== PREVIEW_KIND_IMAGE) {
        return (
            <PreviewPlaceholder
                icon={<ExclamationTriangleIcon className="w-12 h-12 text-yellow-600"/>}
                message={translate('text.unsupported_preview_file_type')}
            />
        );
    }

    return (
        <div className="flex flex-col h-full min-h-0">
            <div className="flex items-center gap-2 p-2 border-b border-tm-gray-200 shrink-0">
                <ButtonIcon
                    tooltip={translate('text.zoom_out')}
                    disabled={zoom <= ZOOM_MIN}
                    onClick={() => setZoom((prev) => clampZoom(prev, -ZOOM_STEP))}
                >
                    <MinusIcon className="w-4 h-4"/>
                </ButtonIcon>

                <button
                    type="button"
                    className="text-sm text-tm-gray-600 hover:text-tm-gray-900"
                    disabled={zoom === 1}
                    onClick={() => setZoom(1)}
                >
                    {Math.round(zoom * 100)}%
                </button>

                <ButtonIcon
                    tooltip={translate('text.zoom_in')}
                    disabled={zoom >= ZOOM_MAX}
                    onClick={() => setZoom((prev) => clampZoom(prev, ZOOM_STEP))}
                >
                    <PlusIcon className="w-4 h-4"/>
                </ButtonIcon>

                <Tooltip content={translate('text.zoom_and_pan_hint')}>
                    <span className="flex items-center text-tm-gray-400 cursor-default px-1">
                        <InformationCircleIcon className="w-5 h-5"/>
                    </span>
                </Tooltip>
            </div>

            {/* min-h-0 is what lets this flex child shrink below its content height so its own
                overflow-auto actually engages — without it the panel grows and the modal body
                scrolls instead, which also leaves ctrl-drag pan with nothing to scroll. */}
            <div ref={containerRef} className="flex-1 min-h-0 overflow-auto bg-tm-gray-100">
                <div className="flex items-start justify-center p-2 min-h-full">
                    {isPdf ? (
                        <PDFDocument
                            file={file}
                            onLoadSuccess={({numPages}) => onPagesNumberChange(numPages)}
                        >
                            <PDFPage
                                pageNumber={currentPage}
                                scale={zoom}
                                renderTextLayer={true}
                                renderAnnotationLayer={false}
                            />
                        </PDFDocument>
                    ) : (
                        <img
                            src={fileUrl}
                            alt={translate('text.document_preview')}
                            draggable={false}
                            style={{transform: `scale(${zoom})`, transformOrigin: 'top center'}}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
